import { Component, isValidElement } from "react";
import { connect } from "react-redux";

import { ToastWrapper, ToastWrapperProps } from "@livechat/design-system-react-components";

import { IStoreState } from "interfaces/storeState";
import { ToastsActions } from "store/features/toasts";
import { IToastDetails, ToastHorizontalPosition, ToastVerticalPosition } from "store/features/toasts/interfaces";
import { getToasts } from "store/features/toasts/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";

export interface IProps {
  verticalPosition: ToastVerticalPosition;
  horizontalPosition: ToastHorizontalPosition;
  toasts: {
    [id: string]: IToastDetails;
  };
  fixed?: boolean;
  name?: string;
  animationType?: string;
  onToastClose(id: string): void;
}

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

const parseContent = (content: IToastDetails["content"]): ArrayElement<ToastWrapperProps["toasts"]>["content"] => {
  if (typeof content === "number") {
    return content.toString();
  }

  if (typeof content === "boolean") {
    return "";
  }

  if (typeof content === "string" || isValidElement(content)) {
    return content;
  }

  return "";
};

const parseVariant = (variant: IToastDetails["variant"]): ArrayElement<ToastWrapperProps["toasts"]>["kind"] => {
  switch (variant) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "info":
    default:
      return "info";
  }
};

export class ToastConsumer extends Component<IProps> {
  getSingleToastProps = (toast: IToastDetails): ArrayElement<ToastWrapperProps["toasts"]> => {
    return {
      id: toast.id,
      kind: parseVariant(toast.variant),
      removable: toast.removable,
      content: parseContent(toast.content),
      onClose: () => this.props.onToastClose(toast.id),
      action: toast.action
        ? {
            label: toast.action.label,
            onClick: toast.action.handler,
            closesOnClick: toast.action.closeOnClick,
          }
        : undefined,
    };
  };

  validateToast = (toast: IToastDetails): boolean => {
    if ((this.props.name || toast.consumerName) && this.props.name !== toast.consumerName) {
      return false;
    }
    if (
      this.props.verticalPosition !== toast.verticalPosition ||
      this.props.horizontalPosition !== toast.horizontalPosition
    ) {
      return false;
    }
    return true;
  };

  render(): JSX.Element {
    const { toasts } = this.props;

    return (
      <ToastWrapper
        fixed
        toasts={[...Object.values(toasts).filter(this.validateToast).map(this.getSingleToastProps)]}
        css={{ zIndex: StackingContextLevel.FullOverlay }}
      />
    );
  }
}

const mapStateToProps = (state: IStoreState): Pick<IProps, "toasts"> => ({
  toasts: getToasts(state),
});

const mapDispatchToProps = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onToastClose: (id: string) => ToastsActions.removeToast({ id }),
};

export const ToastContainer = connect(mapStateToProps, mapDispatchToProps)(ToastConsumer);

export { type IProps as ToastContainerProps };
